/**
 * @fileoverview As regras de acesso dos itens de navegação, num lugar só para o
 * menu do computador (MainListItems), o do celular (BottomNavigation) e as
 * rotas (routes/index.js): cada item diz a permissão do catálogo da tela dele (a
 * mesma que a API confere) e a regra de hoje, que vale sem a regra nova ligada
 * na empresa. Antes, cada menu repetia as condições.
 */

import { useCallback } from "react";
import usePermission from "../hooks/usePermission";
import { allows } from "../components/Can";
import { isInternalOnly, isAttendanceOnly } from "../utils/accessScope";

const attendance = (user) => !isInternalOnly(user);
const admin = (user) => allows(user, "drawer-admin-items:view");
const adminAttendance = (user) => admin(user) && attendance(user);
const reports = (user) => allows(user, "dashboard:view") && attendance(user);
// Quem vê algum departamento abre o Dashboard (só os números deles), nos dois
// modos: o departamento basta.
const departments = (user, extra) => attendance(user) && !!extra?.hasDepartments;

// Configurações aparece com a permissão de alguma seção (a de Segurança, a
// pessoal, é de todos, mas sozinha não põe o item no menu).
const SETTINGS_SECTIONS = [
  "settings.manage",
  "schedules.manage",
  "company.manage",
  "team.manage",
  "company.security.manage",
  "billing.manage",
];

// A empresa de um grupo abre os números do grupo (a API pede reports.view_group).
export const multiUnitPermission = (user) => (user?.company?.groupName ? "reports.view_group" : "reports.view");

// item → [a permissão do catálogo (a função que a escolhe pelo usuário, ou a
// lista em que qualquer uma basta), a regra de hoje (user, extras do menu), e o
// que também libera o item em qualquer modo (opcional)]
export const NAV_ACCESS = {
  tickets: ["tickets.view", attendance],
  contacts: ["tickets.view", attendance],
  quickMessages: ["tickets.view", attendance],
  tags: ["tickets.view", attendance],
  dashboard: ["reports.view", reports, departments],
  multiUnit: [multiUnitPermission, reports],
  // Hoje o menu olha o CRM do plano; a permissão já tem esse teto.
  crm: ["crm.use", (_user, extra) => !!extra?.planHasLeads],
  internalChat: ["internal_chat.use", (user) => !isAttendanceOnly(user)],
  connections: ["connections.manage", adminAttendance],
  queues: ["queues.manage", adminAttendance],
  users: ["team.view", admin],
  flows: ["flows.view", adminAttendance],
  metaTemplates: ["templates.manage", adminAttendance],
  smartApproaches: ["smart_approaches.view", adminAttendance],
  integrations: ["integrations.manage", adminAttendance],
  workOrders: ["work_orders.view", adminAttendance],
  settings: [SETTINGS_SECTIONS, admin],
};

/** A permissão do catálogo do item (ou a lista delas), para este usuário. */
export const navPermission = (item, user) => {
  const [permission] = NAV_ACCESS[item];
  return typeof permission === "function" ? permission(user) : permission;
};

/** visible(item, extras): o item aparece para esta pessoa (com a regra nova ligada, ou a de hoje). */
const useNavigation = (user) => {
  const { can, hasDepartments } = usePermission();
  return useCallback(
    (item, extra) => {
      const [, rule, also] = NAV_ACCESS[item];
      const context = { hasDepartments, ...extra };
      const before = rule(user, context);
      return [].concat(navPermission(item, user)).some((key) => can(key, before)) || (!!also && also(user, context));
    },
    [can, hasDepartments, user]
  );
};

export default useNavigation;
